import React from 'react';
import { StyleSheet, Text, Link } from '@react-pdf/renderer';

// Styles and helpers shared by the receipt PDFs (ThermalReceiptPdf, ThermalReturnReceiptPdf).

// Font size is a receipt-template setting (small/normal/large) applied to
// this PDF and the on-screen preview only -- NOT the raw ESC/POS thermal
// print path, which is a fixed 42-char grid where a bigger font would mean
// fewer characters fit per line (see buildEscposLines.js). Every fontSize in
// BASE_STYLES is defined at "normal" (1x); scaleFontSizes multiplies them
// all by the same factor so the whole receipt scales together, preserving
// the existing size hierarchy (headers still bigger than body text) without
// hand-tuning every value per scale.
const FONT_SCALE = { small: 0.9, normal: 1, large: 1.15 };

function scaleFontSizes(styleObject, factor) {
  return Object.fromEntries(
    Object.entries(styleObject).map(([key, rule]) => [
      key,
      rule.fontSize ? { ...rule, fontSize: rule.fontSize * factor } : rule,
    ])
  );
}

const BASE_STYLES = {
  page: {
    paddingTop: 12,
    paddingBottom: 12,
    paddingHorizontal: 10,
    fontSize: 9,
    fontFamily: 'Courier',
    color: '#111111',
  },
  center: {
    textAlign: 'center',
  },
  storeName: {
    fontSize: 16,
    fontWeight: 700,
  },
  orgName: {
    marginTop: 4,
    fontSize: 10,
  },
  small: {
    marginTop: 2,
    fontSize: 9,
    lineHeight: 1.3,
  },
  divider: {
    borderTopWidth: 1,
    borderTopColor: '#111111',
    borderTopStyle: 'dashed',
    marginVertical: 8,
  },
  title: {
    textAlign: 'center',
    fontSize: 13,
    fontWeight: 700,
  },
  row: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: 8,
  },
  rowLabel: {
    flex: 1,
  },
  rowValue: {
    flexShrink: 0,
    textAlign: 'right',
  },
  tableHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D1D5DB',
    paddingBottom: 3,
    marginBottom: 4,
    fontSize: 8,
    fontWeight: 700,
  },
  tableRow: {
    flexDirection: 'row',
    marginBottom: 4,
  },
  // Rate/Amt are wide enough (with a real paddingRight gap on Rate) for a
  // 5-digit rupee value at the body row's font size (9pt Courier, 5.4pt/char)
  // -- at the old 18%/18% split with no gap, a plain 4-digit price like
  // "1695.00" (7 chars, 37.8pt) already slightly overflowed its 37.2pt box,
  // and with zero space between the two right-aligned columns, Rate and Amt
  // would visually run together edge to edge.
  colSr: { width: '8%' },
  colItem: { width: '36%', paddingRight: 4 },
  // Qty is always a plain integer capped at 100 ("100" = 3 chars, 16.2pt at
  // 9pt Courier), so it needs far less room than Rate/Amt -- the freed width
  // goes to colRate instead of sitting as unused whitespace before it.
  colQty: { width: '9%' },
  colRate: { width: '25%', textAlign: 'right', paddingRight: 4 },
  colAmt: { width: '22%', textAlign: 'right' },
  subtle: {
    fontSize: 7,
    marginTop: 1,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    marginBottom: 4,
  },
  paymentRow: {
    marginBottom: 3,
  },
  gstHeader: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#D1D5DB',
    paddingBottom: 3,
    marginBottom: 4,
    fontSize: 7.5,
    fontWeight: 700,
  },
  gstRow: {
    flexDirection: 'row',
    marginBottom: 3,
    fontSize: 7.5,
  },
  gstColTax: { width: '24%' },
  gstCol: { width: '19%', textAlign: 'right' },
  terms: {
    paddingLeft: 10,
  },
  term: {
    fontSize: 7.5,
    lineHeight: 1.45,
    marginBottom: 2,
  },
  thanks: {
    textAlign: 'center',
    marginTop: 10,
    fontSize: 10,
    fontWeight: 700,
  },
  savings: {
    color: '#166534',
    fontWeight: 700,
  },
  logo: {
    maxWidth: 90,
    maxHeight: 45,
    marginBottom: 4,
    alignSelf: 'center',
  },
  declaration: {
    fontSize: 7.5,
    lineHeight: 1.4,
    textAlign: 'justify',
  },
  handle: {
    textAlign: 'center',
    fontSize: 8,
    marginTop: 2,
  },
  handleLink: {
    color: '#1A0DAB',
    textDecoration: 'underline',
  },
};

const styleSheetCache = {};
export function getStyles(fontSize) {
  const factor = FONT_SCALE[fontSize] ?? FONT_SCALE.normal;
  if (!styleSheetCache[fontSize]) {
    styleSheetCache[fontSize] = StyleSheet.create(scaleFontSizes(BASE_STYLES, factor));
  }
  return styleSheetCache[fontSize];
}

export const money = (value) => `Rs. ${parseFloat(value || 0).toFixed(2)}`;

export const getDateParts = (value) => {
  if (!value) return { date: '', time: '' };
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return { date: '', time: '' };
  return {
    date: date.toLocaleDateString('en-GB'),
    time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false }),
  };
};

// Website / Instagram line: a tappable link when the text reads as one.
export const HandleLine = ({ text, url, styles }) => (
  <Text style={styles.handle}>
    {url ? <Link src={url} style={styles.handleLink}>{text}</Link> : text}
  </Text>
);
